<template>
    <div class="space-y-6">
        <header>
            <Heading
                :level="1"
                title="Permissions Management"
                subtitle="Manage permissions assigned to the organization."
            />
        </header>

        <Card class="rounded">
            <CardHeader>
                <CardTitle>{{ getFromDictionary(`permissions`, 'label') }}</CardTitle>
                <p class="text-sm text-muted-foreground">{{ getFromDictionary(`permissions`, 'description') }}</p>
            </CardHeader>

            <CardContent class="space-y-4">
                <div v-if="canManuallySetPermissions" class="mb-6">
                    <DropdownSelect v-model="state.preset" @update:model-value="onPresetChange">
                        <SelectTrigger class="w-full xl:w-1/2">
                            <SelectValue placeholder="Select a preset">
                                {{$lodash.find(options.presets, (item) => item.key === state.preset)?.label}}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectItem v-for="preset in options.presets" :key="preset.key" :value="preset.key">
                                    {{ preset.label }}
                                </SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </DropdownSelect>
                </div>

                <div class="mb-6 font-semibold">Assignable Permissions</div>
                <PermissionsTable
                    :is-loading="!state.permissions.isLoaded"
                    :available-permissions="availableNonLimitationPermissions"
                    :granted-permissions="grantedNonLimitationPermissions"
                    @change-permission="onChangePermission"
                />

                <div v-if="state.hasSubscription" class="space-y-6 mt-6">
                    <div>
                        <div class="font-semibold">Permissions included in subscription</div>
                        <div class="mb-2 text-xs">(Currently permissions managed by a subscription plan cannot be changed)</div>
                        <PermissionsTable
                            :is-loading="!state.permissions.isLoaded"
                            :available-permissions="subscriptionAssignedPermissions"
                            :granted-permissions="grantedNonLimitationPermissions"
                            :enabled="false"
                        />
                    </div>

                    <div>
                        <div class="font-semibold">Permissions not included in subscription</div>
                        <div class="mb-2 text-xs">(Currently permissions managed by a subscription plan cannot be changed)</div>
                        <PermissionsTable
                            :is-loading="!state.permissions.isLoaded"
                            :available-permissions="subscriptionNonAssignedPermissions"
                            :granted-permissions="grantedNonLimitationPermissions"
                            :enabled="false"
                        />
                    </div>
                </div>
            </CardContent>
        </Card>

        <Card class="rounded">
            <CardHeader>
                <CardTitle>{{ getFromDictionary(`permissions.limitations`, 'label') }}</CardTitle>
                <p class="text-sm text-slate-600">{{ getFromDictionary(`permissions.limitations`, 'description') }}</p>
            </CardHeader>

            <CardContent class="space-y-4">
                <div v-if="canManuallySetPermissions" class="mb-6">
                    <DropdownSelect v-model="state.permissions.limitations.role" @update:model-value="onLimitPresetChange">
                        <SelectTrigger class="w-full xl:w-1/2">
                            <SelectValue placeholder="Select a limit preset">
                                {{$lodash.find(availableLimitationPermissions, (item) => item.key === state.permissions.limitations.role)?.label}}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectItem v-for="permission in availableLimitationPermissions" :key="permission.key" :value="permission.key">
                                    {{ permission.label }}
                                </SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </DropdownSelect>
                </div>

                <div class="mb-6 font-semibold">Usage Limitations</div>

                <div class="w-full overflow-x-auto">
                    <table class="w-full min-w-[600px] table-auto">
                        <thead class="rounded-sm bg-slate-100 text-xs uppercase text-slate-400">
                            <tr>
                                <th class="whitespace-nowrap px-4 py-3 first:pl-5 last:pr-5">
                                    <div class="text-left font-semibold">Limitation</div>
                                </th>
                                <th class="whitespace-nowrap px-4 py-3 first:pl-5 last:pr-5">
                                    <div class="text-left font-semibold">Value</div>
                                </th>
                                <th class="whitespace-nowrap px-4 py-3 first:pl-5 last:pr-5">
                                    <div class="text-right font-semibold">Action</div>
                                </th>
                            </tr>
                        </thead>

                        <tbody v-if="isLoading" class="divide-y divide-slate-200 text-sm">
                            <tr v-for="index in 5" :key="index">
                                <td class="whitespace-nowrap px-4 py-3 first:pl-5 last:pr-5 min-w-[150px]">
                                    <div class="h-3 w-full animate-pulse rounded bg-slate-200" />
                                </td>
                                <td class="px-4 py-3 first:pl-5 last:pr-5 min-w-[250px]">
                                    <div class="h-3 w-full animate-pulse rounded bg-slate-200" />
                                </td>
                                <td class="whitespace-nowrap px-4 py-3 first:pl-5 last:pr-5 min-w-[120px]">
                                    <div class="h-3 w-full animate-pulse rounded bg-slate-200" />
                                </td>
                            </tr>
                        </tbody>

                        <tbody v-else class="divide-y divide-slate-200 text-sm">
                            <tr v-for="limit in options.limits" :key="limit.key">
                                <td class="whitespace-nowrap px-4 py-3 first:pl-5 last:pr-5 min-w-[150px]">
                                    {{ limit.label }}
                                </td>
                                <td class="px-4 py-3 first:pl-5 last:pr-5 min-w-[250px]">
                                    <span v-if="!state.permissions.limitations.limits[limit.key].enabled">Unlimited</span>
                                    <input
                                        v-else
                                        v-model="state.permissions.limitations.limits[limit.key].value"
                                        type="number"
                                        class="flex h-8 w-full sm:w-24 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        :onchange="onLimitChange"
                                    />
                                </td>
                                <td class="whitespace-nowrap px-4 py-3 first:pl-5 last:pr-5 min-w-[120px]">
                                    <div class="flex justify-end">
                                        <div class="form-switch">
                                            <input
                                                :id="limit.key"
                                                v-model="state.permissions.limitations.limits[limit.key].enabled"
                                                type="checkbox"
                                                class="sr-only"
                                                :onchange="onLimitChange"
                                            />
                                            <label class="bg-slate-400" :for="limit.key">
                                                <span class="bg-white shadow-sm" aria-hidden="true"></span>
                                                <span class="sr-only">Toggle</span>
                                            </label>
                                        </div>
                                    </div>
                                </td>
                            </tr>

                            <tr v-for="quota in options.quotas" :key="quota.key">
                                <td class="whitespace-nowrap px-4 py-3 first:pl-5 last:pr-5 min-w-[150px]">
                                    {{ quota.label }}
                                </td>
                                <td class="px-4 py-3 first:pl-5 last:pr-5 min-w-[250px]">
                                    <span v-if="!state.permissions.quotas[quota.key].enabled">{{ state.permissions.quotas[quota.key].defaultValue }}</span>
                                    <input
                                        v-else
                                        v-model="state.permissions.quotas[quota.key].value"
                                        type="number"
                                        class="flex h-8 w-full sm:w-24 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        :onchange="onLimitChange"
                                    />
                                </td>
                                <td class="whitespace-nowrap px-4 py-3 first:pl-5 last:pr-5 min-w-[120px]">
                                    <div class="flex justify-end">
                                        <div class="form-switch">
                                            <input
                                                :id="quota.key"
                                                v-model="state.permissions.quotas[quota.key].enabled"
                                                type="checkbox"
                                                class="sr-only"
                                                :onchange="onLimitChange"
                                            />
                                            <label class="bg-slate-400" :for="quota.key">
                                                <span class="bg-white shadow-sm" aria-hidden="true"></span>
                                                <span class="sr-only">Toggle</span>
                                            </label>
                                        </div>
                                    </div>
                                </td>
                            </tr>

                            <tr>
                                <td class="whitespace-nowrap px-4 py-3 first:pl-5 last:pr-5 min-w-[150px]">
                                    {{ options.seats.label }}
                                </td>
                                <td class="px-4 py-3 first:pl-5 last:pr-5 min-w-[250px]">
                                    <span v-if="!state.permissions.seats.enabled || !canManuallySetPermissions">{{ state.permissions.seats.defaultValue }}</span>
                                    <input
                                        v-else
                                        v-model="state.permissions.seats.value"
                                        type="number"
                                        class="flex h-8 w-full sm:w-24 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        :onchange="onLimitChange"
                                    />
                                </td>
                                <td class="whitespace-nowrap px-4 py-3 first:pl-5 last:pr-5 min-w-[120px]">
                                    <div class="flex justify-end">
                                        <div v-if="canManuallySetPermissions" class="form-switch">
                                            <input
                                                :id="options.seats.key"
                                                v-model="state.permissions.seats.enabled"
                                                type="checkbox"
                                                class="sr-only"
                                                :onchange="onLimitChange"
                                            />
                                            <label class="bg-slate-400" :for="options.seats.key">
                                                <span class="bg-white shadow-sm" aria-hidden="true"></span>
                                                <span class="sr-only">Toggle</span>
                                            </label>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td class="whitespace-nowrap px-4 py-3 first:pl-5 last:pr-5 min-w-[150px]">
                                    {{ options.legacyBilling.label }}
                                </td>
                                <td class="px-4 py-3 first:pl-5 last:pr-5 min-w-[250px]">
                                    <span>{{!canManuallySetPermissions ? "Legacy billing cannot be enabled with an active subscription" : ""}}</span>
                                </td>
                                <td class="whitespace-nowrap px-4 py-3 first:pl-5 last:pr-5 min-w-[120px]">
                                    <div class="flex justify-end">
                                        <div v-if="canManuallySetPermissions" class="form-switch">
                                            <input
                                                :id="options.legacyBilling.key"
                                                v-model="state.permissions.legacyBilling.enabled"
                                                :disabled="!canManuallySetPermissions"
                                                type="checkbox"
                                                class="sr-only"
                                                :onchange="onLimitChange"
                                            />
                                            <label class="bg-slate-400" :for="options.legacyBilling.key">
                                                <span class="bg-white shadow-sm" aria-hidden="true"></span>
                                                <span class="sr-only">Toggle</span>
                                            </label>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </CardContent>
        </Card>
    </div>
</template>


<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Heading } from '@/components/ui/typography'
import PermissionsTable from '@/partials/tables/PermissionsTable.vue'
import permissionsAPI from '@/utils/api/api.permissions'
import subscriptionsAPI from '../utils/api/api.subscriptions'

export default {
    name: 'OrganizationPermissionsSubpage',

    components: {
        DropdownSelect: Select,
        SelectContent,
        SelectGroup,
        SelectItem,
        SelectTrigger,
        SelectValue,
        PermissionsTable,
        Card,
        CardContent,
        CardHeader,
        CardTitle,
        Heading,
    },

    props: {
        organization: {
            type: Object,
        },
    },

    data() {
        return {
            permissions: {
                availablePermissions: [],
                grantedPermissions: [],
            },
            state: {
                permissions: {
                    isLoaded: false,
                    limitations: {
                        role: 'none',
                        limits: {
                            'lists.total': {
                                key: 'lists.total',
                                value: null,
                                enabled: false,
                            },
                            'lists.size': {
                                key: 'lists.size',
                                value: null,
                                enabled: false,
                            },
                        },
                    },
                    quotas: {
                         'profile-unlock': {
                            key: 'profile-unlock',
                            value: 0,
                            enabled: false,
                            defaultValue : 0,
                        },
                        'report-generation': {
                            key: 'report-generation',
                            value: 0,
                            enabled: false,
                            defaultValue : 0,
                        },
                    },
                    seats : {
                        key: 'seats',
                        value: 0,
                        enabled: false,
                    },
                    legacyBilling : {
                        key: 'legacy-billing',
                        value: null,
                        enabled: false,
                    }
                },
                preset: 'custom',
                hasSubscription : false,
                subscriptionManagedPermissions : [
                    'module.discovery',
                    'module.lists',
                    'module.messages',
                    'module.reports',
                    'module.chatbot',
                    'feature.global.emails',
                    'feature.global.creator_search',
                    'feature.discovery.twitch',
                    'feature.discovery.youtube',
                    'feature.discovery.tiktok',
                    'feature.discovery.twitter',
                    'feature.discovery.instagram',
                    'feature.lists.export',
                    'feature.ai.bio',
                    'feature.audience.demographics',
                    'feature.audience.interests',
                    'feature.campaigns.manage',
                    'usage.tier.tiny',
                    'usage.tier.small',
                    'usage.tier.medium',
                    'usage.tier.large',
                    'data.timerange.triennial',
                ],
            },
            options: {
                presets: [
                    {
                        key: 'custom',
                        label: 'Custom',
                    },
                    {
                        key: 'nothing',
                        label: 'Nothing',
                        permissions: [],
                    },
                    {
                        key: 'everything',
                        label: 'Everything',
                    },
                    {
                        key: 'enterprise-paid',
                        label: 'Agency Service (Paid)',
                        permissions: ['module.campaigns', 'feature.ai.bio'],
                    },
                ],
                limits: [
                    { key: 'lists.total', label: 'Maximum number of lists' },
                    { key: 'lists.size', label: 'Maximum creators per list' },
                ],
                quotas: [
                    { key: 'profile-unlock', label: 'Quota for profile unlocks' },
                    { key: 'report-generation', label: 'Quota for report generation' },
                ],
                seats: { key: 'seats', label: 'Seats' },
                legacyBilling: { key: 'legacy-billing', label: 'Legacy billing' },
            },
        }
    },

    computed: {
        ...mapGetters([
            'getFromDictionary',
            'isSfStaff',
            'allPermissions',
            'selfActiveOrganizationId',
            'defaultDataLimits',
            'selfActiveOrganization',
        ]),
        grantedNonLimitationPermissions() {
            return _.filter(this.permissions.grantedPermissions, (permission) => {
                return !permission.startsWith('usage.')
            })
        },
        availableNonLimitationPermissions() {
            return _.filter(this.permissions.availablePermissions, (permission) => {
                return !permission.startsWith('usage.') && (this.canManuallySetPermissions || !this.state.subscriptionManagedPermissions.includes(permission))
            })
        },
        subscriptionAssignedPermissions() {
            return _.filter(this.permissions.availablePermissions, (permission) => {
                return !permission.startsWith('usage.') && (this.state.subscriptionManagedPermissions.includes(permission)) && this.permissions.grantedPermissions.includes(permission)
            })
        },
        subscriptionNonAssignedPermissions() {
            return _.filter(this.permissions.availablePermissions, (permission) => {
                return !permission.startsWith('usage.') && (this.state.subscriptionManagedPermissions.includes(permission)) && !this.permissions.grantedPermissions.includes(permission)
            })
        },
        grantedLimitationPermissions() {
            return _.filter(this.permissions.grantedPermissions, (permission) => {
                return permission.startsWith('usage.')
            })
        },
        availableLimitationPermissions() {
            const limitationPermissions = this.availableLimitPermissions()

            limitationPermissions.unshift({
                key: 'none',
                label: 'None',
                description: 'No limitations applied',
            })

            limitationPermissions.push({
                key: 'custom',
                label: 'Custom',
                description: 'Custom limitations applied',
            })

            return limitationPermissions
        },
        canManuallySetPermissions() {
            return !this.state.hasSubscription
        },
    },

    watch: {
        isSfStaff(value) {
            if (value === false) {
                this.$router.push({ name: 'landing' })
            }
        },
    },

    created() {
        this.state.permissions.isLoaded = false
        this.getOrganizationPermissions()
        this.getPermissionSets()
        this.getOrganziationSubscriptions()
        this.state.permissions.seats.value = this.selfActiveOrganization.seats
        this.state.permissions.legacyBilling.enabled = this.selfActiveOrganization.is_legacy_billing === true
    },

    methods: {
        syncToPreset() {
            let presetType = 'custom'

            const sortedAvailablePermissions = _.sortBy(this.permissions.grantedPermissions).join('_')

            _.forEach(this.options.presets, (preset) => {
                const sortedPresetPermissions = _.sortBy(preset.permissions).join('_')

                if (sortedAvailablePermissions === sortedPresetPermissions) {
                    presetType = preset.key
                }
            })

            this.state.preset = presetType

            this.syncLimitPreset()
        },

        syncLimitPreset() {
            const assignedLimitPreset = _.find(this.permissions.grantedPermissions, (permission) => {
                return permission.startsWith('usage.')
            })

            if (assignedLimitPreset) {
                this.state.permissions.limitations.role = assignedLimitPreset
            } else if (this.state.permissions.limitations.role !== 'custom') {
                this.state.permissions.limitations.role = 'none'
            }

            this.setLimitsFromPreset(this.state.permissions.limitations.role)
        },

        async onPresetChange(presetKey) {
            const preset = _.find(this.options.presets, (item) => item.key === presetKey)

            if (this.isSfStaff && preset) {
                await permissionsAPI.setPermissions(this.$axios, this.selfActiveOrganizationId, preset.permissions)
                this.getOrganizationPermissions()
            }
        },

        async onLimitPresetChange(limitRole) {
            this.setLimitsFromPreset(limitRole)
            this.onLimitChange()
        },

        async onChangePermission(permission, grantType) {
            if (grantType === 'grant') {
                await this.grantPermission(permission)
            } else if (grantType === 'deny') {
                await this.denyPermission(permission)
            }

            await this.getOrganizationPermissions()
        },

        availableLimitPermissions() {
            let limitPermissions = _.filter(this.permissions.availablePermissions, (permission) => {
                return permission.startsWith('usage.')
            })

            limitPermissions = _.map(limitPermissions, (permission) => {
                return {
                    key: permission,
                    label: this.getFromDictionary(`permissions.['${permission}']`, 'label'),
                    description: this.getFromDictionary(`permissions.['${permission}']`, 'description'),
                }
            })

            return limitPermissions
        },

        async addLimitRole() {
            const existingLimitPermissions = _.filter(this.permissions.grantedPermissions, (permission) => {
                return permission.startsWith('usage.')
            })

            this.permissions.grantedPermissions = _.filter(this.permissions.grantedPermissions, (permission) => {
                return !permission.startsWith('usage.')
            })
            if (!['none', 'custom'].includes(this.state.permissions.limitations.role)) {
                this.permissions.grantedPermissions.push(this.state.permissions.limitations.role)
            }

            const updatedLimitPermissions = _.filter(this.permissions.grantedPermissions, (permission) => {
                return permission.startsWith('usage.')
            })

            let somethingChanged = false

            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < existingLimitPermissions.length; i++) {
                const existing = existingLimitPermissions[i]
                if (!updatedLimitPermissions.includes(existing)) {
                    // eslint-disable-next-line no-await-in-loop
                    await this.denyPermission(existing)
                    somethingChanged = true
                }
            }

            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < updatedLimitPermissions.length; i++) {
                const updated = updatedLimitPermissions[i]
                if (!existingLimitPermissions.includes(updated)) {
                    // eslint-disable-next-line no-await-in-loop
                    await this.grantPermission(updated)
                    somethingChanged = true
                }
            }

            if (somethingChanged) {
                this.getOrganizationPermissions()
            }
        },

        setLimitsFromPreset(limitPreset) {
            const defaults = this.defaultDataLimits

            if (limitPreset === 'none') {
                _.forEach(this.state.permissions.limitations.limits, (limit) => {
                    limit.enabled = false
                    limit.value = null
                })
            } else if (limitPreset === 'custom') {
                // No change
            } else {
                const presetLimits = defaults[limitPreset.replace('usage.tier.', '')]
                _.forEach(this.state.permissions.limitations.limits, (limit) => {
                    const limitSetting = _.find(presetLimits, (presetLimit) => {
                        return presetLimit.key === limit.key
                    })

                    if (limitSetting && limitSetting.value) {
                        limit.enabled = true
                        limit.value = limitSetting.value
                    } else {
                        limit.enabled = false
                        limit.value = null
                    }
                })
            }

            this.addLimitRole()
        },

        async onLimitChange() {
            this.state.permissions.limitations.role = 'custom'
            const defaults = this.defaultDataLimits

            let allNone = true
            const limitPermissions = _.map(this.availableLimitPermissions(), (limit) => {
                return limit.key.replace('usage.tier.', '')
            })

            const matchedLimitPermissions = {}

            _.forEach(limitPermissions, (limitPermission) => {
                matchedLimitPermissions[limitPermission] = true
            })

            let limitationsToSave = []

            _.forEach(this.state.permissions.limitations.limits, (limit) => {
                if (limit.enabled && limit.value && limit.value !== -1) {
                    allNone = false
                    limitationsToSave.push({
                        key: limit.key,
                        value: limit.value,
                    })
                }

                _.forEach(limitPermissions, (limitPermission) => {
                    let permisionValue = _.get(defaults[limitPermission], limit.key)
                    let newValue = null

                    if (limit.enabled && limit.value && limit.value !== -1) {
                        newValue = limit.value
                    }

                    if (permisionValue === -1) {
                        permisionValue = null
                    }

                    if (permisionValue !== newValue) {
                        matchedLimitPermissions[limitPermission] = false
                    }
                })
            })

            let isNotCustom = false

            if (allNone) {
                this.state.permissions.limitations.role = 'none'
                isNotCustom = true
            }

            _.forEach(limitPermissions, (limitPermission) => {
                if (matchedLimitPermissions[limitPermission] === true) {
                    this.state.permissions.limitations.role = `usage.tier.${limitPermission}`
                    isNotCustom = true
                }
            })

            this.addLimitRole()

            if (isNotCustom) {
                limitationsToSave = []
            }

            // Quotas
            _.forEach(this.state.permissions.quotas, (quota) => {
                if (quota.enabled && quota.value && quota.value !== -1) {
                    limitationsToSave.push({
                        key: quota.key,
                        value: quota.value,
                    })
                }
            })

            // Seats
            if (this.state.permissions.seats.enabled && this.state.permissions.seats.value && this.state.permissions.seats.value !== -1) {
                limitationsToSave.push({
                    key: this.state.permissions.seats.key,
                    value: this.state.permissions.seats.value,
                })
            }

            // Legacy billing
            limitationsToSave.push({
                key: this.state.permissions.legacyBilling.key,
                value: this.state.permissions.legacyBilling.enabled,
            })

            await this.saveLimitations(limitationsToSave)

            this.selfActiveOrganization.seats = this.state.permissions.seats.value
            this.selfActiveOrganization.is_legacy_billing = this.state.permissions.legacyBilling.enabled

        },

        async getOrganizationPermissions() {
            if (this.isSfStaff) {
                const result = await permissionsAPI.getOrganizationPermissions(
                    this.$axios,
                    this.selfActiveOrganizationId,
                )

                if (result.success) {
                    this.state.permissions.isLoaded = true

                    const everythingPreset = _.find(this.options.presets, (preset) => preset.key === 'everything')

                    // Grab everything other than usage tiers, which means the user has unlimited everything
                    everythingPreset.permissions = _.filter(
                        result.value.availablePermissions,
                        (item) => !item.startsWith('usage'),
                    )

                    this.permissions.availablePermissions = result.value.availablePermissions
                    this.permissions.grantedPermissions = result.value.grantedPermissions

                    this.syncToPreset()
                    if (result.value.limitations) {
                        _.forEach(result.value.limitations, (limit) => {
                            if (limit.value && limit.value !== -1) {
                                if (this.state.permissions.limitations.limits[limit.key]) {
                                    this.state.permissions.limitations.limits[limit.key].value = limit.value
                                    this.state.permissions.limitations.limits[limit.key].enabled = true
                                    this.state.permissions.limitations.role = 'custom'
                                }
                                else if (this.state.permissions.quotas[limit.key]) {
                                    this.state.permissions.quotas[limit.key].value = limit.value
                                    this.state.permissions.quotas[limit.key].enabled = true
                                }
                                else if (this.state.permissions.seats) {
                                    this.state.permissions.seats.value = limit.value
                                    this.state.permissions.seats.enabled = true
                                }
                            }
                        })
                    }

                    this.getOrganizationQuotas()
                }
            }
        },

        async getOrganizationQuotas() {
            if (this.isSfStaff) {
                this.$axios
                    .get(`/organizations/${this.selfActiveOrganization.id}/quota`, { })
                    .then(({ data }) => {
                        const payload = data.payload
                        if (payload?.quotas) {
                            _.forEach(payload.quotas, quota => {
                                if (this.state.permissions.quotas[quota.type]) {
                                    this.state.permissions.quotas[quota.type].defaultValue = quota.allocated_quota
                                    if (!this.state.permissions.quotas[quota.type].enabled) {
                                        this.state.permissions.quotas[quota.type].value = quota.allocated_quota
                                    }
                                }
                            })
                        }

                    })
                    .catch((error) => {
                        console.error('Error fetching quota data', error)
                    })

            }
        },

        async getPermissionSets() {
            if (this.isSfStaff) {
                const result = await permissionsAPI.getPermissionSets(this.$axios, this.selfActiveOrganizationId)
                _.forOwn(result.value.products, (value) => {
                    this.options.presets.push({
                        key: value.key,
                        label: value.label,
                        permissions: value.roles,
                    })
                })
            }
        },

        async denyPermission(permission) {
            if (this.isSfStaff) {
                await permissionsAPI.denyPermission(this.$axios, this.selfActiveOrganizationId, permission)
            }
        },

        async grantPermission(permission) {
            if (this.isSfStaff) {
                await permissionsAPI.grantPermission(this.$axios, this.selfActiveOrganizationId, permission)
            }
        },

        async saveLimitations(limitations) {
            if (this.isSfStaff) {
                await permissionsAPI.saveLimitations(this.$axios, this.selfActiveOrganizationId, limitations)
            }
        },

        async getOrganziationSubscriptions() {
            const subscribedResult = await subscriptionsAPI.getOrganizationSubscriptions(this.$axios)
            if (subscribedResult.success && subscribedResult.value?.subscriptions?.length > 0) {
                this.state.hasSubscription = true
            }
            else {
                this.state.hasSubscription = false
            }
        },
    },
}
</script>
